import { gql, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import { type FormikProps, useFormik } from 'formik';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useEffect } from 'react';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import SelectWithLabel from 'src/components/SelectWithLabel';
import { Collection, createGalleryFromExistingCollection } from 'src/dynamoQueries/collections';
import { onCreateGallerySuccess, onErrorCreateGallery } from 'src/helpers';
import GalleryTitleInput from '../GalleryTitleInput';

interface MediaGroup {
  id: string;
  name: string;
}

interface GalleryFormValues {
  name: string;
  mediaGroupId: string;
}

interface CollectionData {
  collection: {
    id: string;
    name: string;
  };
}

interface CreateGalleryFromExistingMutationData {
  createGalleryFromExistingCollection: {
    collection: {
      id: string;
    };
  };
}

interface CreateGalleryFromExistingCollectionDialogProps {
  isOpen: boolean;
  mediaGroups: MediaGroup[];
  onClose: () => void;
  collectionId: string;
}

const CreateGalleryFromExistingCollectionDialog = ({
  isOpen,
  mediaGroups,
  onClose,
  collectionId,
}: CreateGalleryFromExistingCollectionDialogProps): React.JSX.Element | null => {
  const navigate = useNavigate();
  const header = 'Create Gallery';

  const buildCreateGalleryFromExistingCollection = () => {
    const m = createGalleryFromExistingCollection(true);
    const mutation = jsonToGraphQLQuery(m, { pretty: true });
    return gql(mutation);
  };

  const [
    createGalleryFromExistingCollectionMutation,
    { data: createdGalleryData, loading: createGalleryLoading, error: errorCreatingGallery },
  ] = useMutation<CreateGalleryFromExistingMutationData>(buildCreateGalleryFromExistingCollection());

  const fetchCollQuery = () => {
    const query = Collection(true);
    return gql(jsonToGraphQLQuery(query, { pretty: true }));
  };
  const {
    data: collectionData,
    loading: collectionLoading,
    error: collectionError,
  } = useQuery<CollectionData>(fetchCollQuery(), { skip: !collectionId, variables: { id: collectionId } });

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate is stable reference from useNavigate
  useEffect(() => {
    if (createdGalleryData?.createGalleryFromExistingCollection) {
      onCreateGallerySuccess({
        navigate,
        galleryId: createdGalleryData?.createGalleryFromExistingCollection?.collection?.id,
      });
    }
  }, [createdGalleryData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: _onClose is stable reference
  useEffect(() => {
    if (errorCreatingGallery) {
      onErrorCreateGallery({ error: errorCreatingGallery });
      _onClose();
      return;
    }
  }, [errorCreatingGallery]);

  const _onClose = () => {
    onClose();
  };

  const onSave = (values: GalleryFormValues) => {
    createGalleryFromExistingCollectionMutation({
      variables: {
        collectionId,
        mediaGroupId: values.mediaGroupId,
        name: values.name,
      },
    });
  };

  const formik: FormikProps<GalleryFormValues> = useFormik<GalleryFormValues>({
    initialValues: {
      name: collectionData?.collection?.name || '',
      mediaGroupId: '',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      onSave(values);
    },
  });

  if (errorCreatingGallery || collectionError) {
    onClose();
    return null;
  }

  return (
    <AkomiDialog
      open={isOpen}
      onClose={_onClose}
      title={header}
      variant="form"
      maxWidth="xs"
      loading={collectionLoading}
      actions={
        <AkomiDialogActions
          variant="confirm"
          primaryLabel="Create Gallery"
          onPrimary={() => formik.handleSubmit()}
          primaryDisabled={!formik.values.mediaGroupId}
          submitting={createGalleryLoading}
        />
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <GalleryTitleInput formik={formik} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <SelectWithLabel
            items={mediaGroups}
            // biome-ignore lint/suspicious/noExplicitAny: SelectWithLabel expects FormikInstance with mediaGroupId, but GalleryFormValues has more generic typing
            formik={formik as any}
            label="Select a Media Group"
            name="mediaGroupId"
          />
        </div>
      </form>
    </AkomiDialog>
  );
};

export default CreateGalleryFromExistingCollectionDialog;
